import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  model,
  OnInit,
  output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { findIndex } from 'lodash';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

import { ColumnChooserModel, ColumnChooserNode } from '../../model/column-chooser.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ReactiveFormsModule,
    FormsModule,
    CheckboxComponent,
    DragDropModule,
    NgTemplateOutlet,
    TooltipDirective,
  ],
  selector: 'aux-column-chooser-items',
  standalone: true,
  styleUrls: ['./column-chooser-items.component.scss'],
  templateUrl: './column-chooser-items.component.html',
})
export class ColumnChooserItemsComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);

  mutableColumns = model<ColumnChooserNode[]>();

  countPinnedColumns = computed(() => {
    function count(nodes: ColumnChooserNode[]): number {
      return nodes.reduce((acc, node) => {
        const selfPinned = node.pinned ? 1 : 0;
        const childrenPinned = node.children ? count(node.children) : 0;

        return acc + selfPinned + childrenPinned;
      }, 0);
    }

    return count(this.mutableColumns() || []);
  });

  columns = input.required<ColumnChooserNode[]>();
  isChildrenNode = input<boolean>(false);
  reorder = input(false);
  searchTerm = input<string>('');
  showPinIcons = input(false);

  childrenChanged = output<ColumnChooserNode[]>();

  ngOnInit(): void {
    this.mutableColumns.set(this.columns());
  }

  deselectAll() {
    this.mutableColumns.update((columns) => this.updateAllChildren(columns || [], false));
  }

  private filterColumns(columns: ColumnChooserNode[], term: string): ColumnChooserNode[] {
    if (!term) return columns;

    return columns
      .map((column) => {
        const matches = column.headerName.toLowerCase().includes(term.toLowerCase());
        let filteredChildren: ColumnChooserNode[] | undefined;

        // If the current node matches, keep all children
        if (matches) {
          return {
            ...column,
            children: column.children, // Keep all children as is
            isCollapsed: false,
          };
        }

        // If current node doesn't match, check children
        if (column.children?.length) {
          filteredChildren = this.filterColumns(column.children, term);

          if (filteredChildren.length > 0) {
            return {
              ...column,
              children: filteredChildren,
              isCollapsed: false,
            };
          }
        }

        return null;
      })
      .filter((column) => column !== null);
  }

  getFilteredColumns(): ColumnChooserNode[] {
    return this.filterColumns(this.mutableColumns() || [], this.searchTerm());
  }

  isRootColumnIndeterminate(column: ColumnChooserNode) {
    if (!(column.root && !!column.children?.length)) return false;

    return (
      ColumnChooserModel.areSomeChildrenChecked(column.children) &&
      !ColumnChooserModel.areAllChildrenChecked(column.children)
    );
  }

  onCheckboxChange(checked: boolean, column: ColumnChooserNode) {
    this.mutableColumns.update((columns) => {
      return this.updateColumnState(columns || [], column.headerName, checked);
    });

    if (this.isChildrenNode()) {
      this.childrenChanged.emit(this.mutableColumns() || []);
    }
  }

  onChildrenChanged(children: ColumnChooserNode[], parentHeaderName: string) {
    this.mutableColumns.update((columns) => {
      return (columns || []).map((column) => {
        if (column.headerName === parentHeaderName) {
          const areAllChildrenChecked = ColumnChooserModel.areAllChildrenChecked(children);
          const areSomeChildrenChecked = ColumnChooserModel.areSomeChildrenChecked(children);

          // To keep persist state in case when filtered columns
          const subColumns = (column.children || []).map((child) => {
            const index = findIndex(children, (item) => item.field === child.field);

            return index === -1 ? child : children[index];
          });

          return {
            ...column,
            checked: areAllChildrenChecked,
            children: subColumns,
            indeterminate: !areAllChildrenChecked && areSomeChildrenChecked,
          };
        }

        return column;
      });
    });
  }

  onDrop(event: CdkDragDrop<ColumnChooserNode[]>) {
    if (event.previousIndex === event.currentIndex || !this.reorder()) return;

    this.mutableColumns.update((columns) => {
      const newColumns = [...(columns || [])];

      moveItemInArray(newColumns, event.previousIndex, event.currentIndex);

      return newColumns;
    });
  }

  selectAll() {
    this.mutableColumns.update((columns) => this.updateAllChildren(columns || [], true));
  }

  toggleCollapse(column: ColumnChooserNode) {
    column.isCollapsed = !column.isCollapsed;
    this.cdr.detectChanges();
  }

  togglePin(column: ColumnChooserNode) {
    this.mutableColumns.update((columns) => {
      return (columns || []).map((c) => {
        if (c === column) {
          return { ...c, pinned: !c.pinned };
        } else {
          return c;
        }
      });
    });

    if (this.isChildrenNode()) {
      this.childrenChanged.emit(this.mutableColumns() || []);
    }
  }

  unpinAll() {
    this.mutableColumns.update((columns) => this.updateAllPinned(columns || [], false));
  }

  private updateAllChildren(children: ColumnChooserNode[], checked: boolean): ColumnChooserNode[] {
    return children.map((child) => ({
      ...child,
      checked,
      children: child.children ? this.updateAllChildren(child.children, checked) : undefined,
      indeterminate: false,
    }));
  }

  updateAllPinned(columns: ColumnChooserNode[], pinned: boolean): ColumnChooserNode[] {
    return columns.map((column) => ({
      ...column,
      children: column.children ? this.updateAllPinned(column.children, pinned) : undefined,
      pinned,
    }));
  }

  private updateColumnState(
    columns: ColumnChooserNode[],
    headerName: string,
    checked: boolean
  ): ColumnChooserNode[] {
    return columns.map((column) => {
      if (column.headerName === headerName) {
        column.checked = checked;

        // Update all children if present
        if (column.children?.length) {
          column.children = this.updateAllChildren(column.children, checked);
        }
      } else if (column.children?.length) {
        // Recursively update children
        column.children = this.updateColumnState(column.children, headerName, checked);
        // Update parent state based on children
        const areAllChildrenChecked = ColumnChooserModel.areAllChildrenChecked(column.children);
        const areSomeChildrenChecked = ColumnChooserModel.areSomeChildrenChecked(column.children);

        column.checked = areAllChildrenChecked;
        column.indeterminate = !areAllChildrenChecked && areSomeChildrenChecked;
      }

      return column;
    });
  }
}
